import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Typography from '@mui/joy/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { ShadowJobComparison } from '@numeo/types-core'
import { useParams } from 'react-router-dom'
import { apiErrorMessage } from '../api'
import { JobDocument, shadowDifferencesApi } from './api'
import DocumentViewer from './DocumentViewer'
import ShadowDeltaTable from './ShadowDeltaTable'
import { useGoBack } from './useGoBack'

/**
 * One job that differs from the legacy chain: the document itself on the left (zoom in, read it whole), what differs field by field on
 * the right. Back returns to the list it was opened from.
 */
export default function ShadowDifferenceDetailPage() {
    const { jobId = '' } = useParams()
    const goBack = useGoBack('/document-pipeline/audit/shadow-differences')
    const [comparison, setComparison] = React.useState<ShadowJobComparison | null>(null)
    const [document, setDocument] = React.useState<JobDocument | null>(null)
    const [comparisonError, setComparisonError] = React.useState<string | null>(null)
    const [documentError, setDocumentError] = React.useState<string | null>(null)

    React.useEffect(() => {
        let current = true
        setComparison(null)
        setDocument(null)
        setComparisonError(null)
        setDocumentError(null)
        // Independent: the document may be gone while the comparison still reads, and the other way round.
        shadowDifferencesApi
            .getDelta(jobId)
            .then((loaded) => current && setComparison(loaded))
            .catch((err) => current && setComparisonError(apiErrorMessage(err, 'Failed to compare this job with the legacy chain')))
        shadowDifferencesApi
            .getDocument(jobId)
            .then((loaded) => current && setDocument(loaded))
            .catch((err) => current && setDocumentError(apiErrorMessage(err, 'Failed to load the document')))
        return () => {
            current = false
        }
    }, [jobId])

    const job = document?.job
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button size="sm" variant="outlined" color="neutral" startDecorator={<ArrowBackIcon />} onClick={goBack}>
                    Back
                </Button>
                <Box sx={{ minWidth: 0 }}>
                    <Typography level="h3" sx={{ wordBreak: 'break-word' }}>
                        {comparison?.filename ?? job?.filename ?? 'Job'}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        {comparison?.projectName ?? job?.project_name} · job {jobId.slice(0, 8)}
                        {comparison?.score != null && ` · agreement score ${comparison.score.toFixed(3)}`}
                    </Typography>
                </Box>
                {comparison?.legacyState && comparison.rateConfId && (
                    <Chip size="sm" variant="soft" color="neutral">
                        legacy: {comparison.legacyState}
                    </Chip>
                )}
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(340px, 5fr) minmax(0, 7fr)' }, alignItems: 'start' }}>
                <Box sx={{ position: { lg: 'sticky' }, top: { lg: 12 }, height: { xs: 480, lg: 'calc(100vh - 110px)' } }}>
                    {documentError && (
                        <Alert color="danger" variant="soft">
                            {documentError}
                        </Alert>
                    )}
                    {!document && !documentError && <CircularProgress size="sm" />}
                    {document && <DocumentViewer url={document.previewUrl} filename={job?.filename ?? null} mimeType={job?.mime_type ?? null} />}
                </Box>
                <Box>
                    {comparisonError && (
                        <Alert color="danger" variant="soft">
                            {comparisonError}
                        </Alert>
                    )}
                    {!comparison && !comparisonError && <CircularProgress size="sm" />}
                    {comparison && <ShadowDeltaTable comparison={comparison} />}
                </Box>
            </Box>
        </Box>
    )
}
